import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/* Table Pagination — Figma benchmark 4180:13254 (1124×72): left "Page X of
   Y" label, center Prev/page-number/Next control group (32px-tall buttons),
   right range label. Page-number buttons are themselves Button-component
   instances per the benchmark; this uses matching sizing (32px) rather than
   the shared Button's own size scale, since none of the existing Button
   sizes (28/36/44px) match the benchmark's 32px pagination control. */

function PageButton({
  active,
  disabled,
  onClick,
  children,
  ariaLabel,
  wide,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      className={cn(
        "grid h-8 shrink-0 place-items-center rounded-md border text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft disabled:cursor-not-allowed disabled:opacity-40",
        wide ? "px-3" : "min-w-8 px-1",
        active ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-text hover:bg-bg"
      )}
    >
      {children}
    </button>
  );
}

function getPageNumbers(page: number, pageCount: number): (number | "ellipsis")[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages = new Set<number>([1, pageCount, page, page - 1, page + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const withEllipsis: (number | "ellipsis")[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) withEllipsis.push("ellipsis");
    withEllipsis.push(p);
  });
  return withEllipsis;
}

/* The range label states a row window only when one really exists. Without a
   total and a page size there is nothing to range over; an empty result set
   inverts the naive window (1..0); and a page outside 1..pageCount would claim
   a window the table is not showing. In all three cases the slot stays in the
   layout as a single space — the three-column header must not reflow between
   pages — and no count is ever invented. A known-empty set is stated plainly,
   because "Showing 0 of 0" is true and "Showing 1–0 of 0" is not. */
function getRangeLabel(page: number, pageSize: number | undefined, totalItems: number | undefined): string {
  if (totalItems === undefined || pageSize === undefined || pageSize <= 0) return " ";
  if (totalItems === 0) return "Showing 0 of 0";
  if (page < 1) return " ";
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  if (start > end) return " ";
  return `Showing ${start}–${end} of ${totalItems}`;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  totalItems,
  pageSize,
  className,
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  className?: string;
}) {
  const pages = getPageNumbers(page, pageCount);
  const rangeLabel = getRangeLabel(page, pageSize, totalItems);

  return (
    <nav
      aria-label="Table pagination"
      className={cn("flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3.5", className)}
    >
      <span className="text-xs font-medium text-muted">
        Page {page} of {pageCount}
      </span>

      <div className="flex items-center gap-1.5">
        <PageButton ariaLabel="Previous page" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft aria-hidden className="size-3.5" />
        </PageButton>
        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span key={`ellipsis-${i}`} className="grid h-8 min-w-8 place-items-center text-xs text-muted">
              …
            </span>
          ) : (
            <PageButton key={p} active={p === page} onClick={() => onPageChange(p)} ariaLabel={`Page ${p}`}>
              {p}
            </PageButton>
          )
        )}
        <PageButton ariaLabel="Next page" disabled={page >= pageCount} onClick={() => onPageChange(page + 1)}>
          <ChevronRight aria-hidden className="size-3.5" />
        </PageButton>
      </div>

      <span className="text-xs font-medium text-muted">{rangeLabel}</span>
    </nav>
  );
}
